"use client"

import { ZentjesLogo } from "@/shared/components/zentjes-logo"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { usePwaInstall } from "@/shared/hooks/use-pwa-install"
import { getPwaInstallPlatform, hasHandledPwaInstallNotice, markPwaInstallNoticeDismissed } from "@/shared/lib/pwa-install"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { X } from "lucide-react"
import { useEffect, useState, type SVGProps } from "react"

export function InstallPrompt() {
    const { isInstallable, install, installMethod } = usePwaInstall()
    const { trigger } = useHaptic()
    const [isVisible, setIsVisible] = useState(false)
    const [isDismissed, setIsDismissed] = useState(true)

    useEffect(() => {
        setIsDismissed(hasHandledPwaInstallNotice(getPwaInstallPlatform()))
    }, [])

    useEffect(() => {
        if (isInstallable && !isDismissed) {
            const timer = setTimeout(() => setIsVisible(true), 2000)
            return () => clearTimeout(timer)
        }
    }, [isInstallable, isDismissed])

    function handleDismiss() {
        trigger("light")
        setIsVisible(false)
        const platform = getPwaInstallPlatform()
        markPwaInstallNoticeDismissed(platform)
        setIsDismissed(true)
    }

    async function handleInstall() {
        trigger("heavy")
        await install()
        setIsVisible(false)
    }

    const title = installMethod === "ios" ? "Install on iOS" : installMethod === "android" ? "Install on Android" : "Install Zentjes"
    const description =
        installMethod === "ios"
            ? "Tap Share, then Add to Home Screen. Zentjes will not show this again after install."
            : installMethod === "android"
              ? "Android supports one-tap install via the browser prompt. Tap Install to add Zentjes instantly."
              : "Install for a focused app window without browser chrome."

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 320, damping: 28 }}
                    className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] left-4 right-4 z-50 md:bottom-6 md:left-auto md:right-6 md:w-[430px]"
                >
                    <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-zinc-950/80 p-3.5 shadow-[0_12px_32px_-14px_rgba(0,0,0,0.75)] backdrop-blur-xl supports-[backdrop-filter]:bg-zinc-950/65">
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_120%_at_0%_0%,rgba(255,255,255,0.08),transparent_48%),radial-gradient(90%_90%_at_100%_100%,rgba(255,255,255,0.05),transparent_55%)]" />
                        <div className="relative flex items-center gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-zinc-900/70">
                                <ZentjesLogo size="sm" />
                            </div>

                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-sm font-semibold tracking-tight text-zinc-100">{title}</h3>
                                <p className="mt-0.5 text-xs leading-4 text-zinc-400">{description}</p>
                            </div>

                            <button
                                onClick={handleDismiss}
                                aria-label="Dismiss install prompt"
                                className="min-h-11 min-w-11 rounded-full p-2 text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-200 active:scale-[0.98]"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {installMethod === "ios" && <IosInstallMicroGuide />}

                        {(installMethod === "android" || installMethod === "web") && (
                            <button
                                onClick={handleInstall}
                                className="relative mt-3 min-h-11 rounded-full border border-white/15 bg-zinc-100 px-4 py-2 text-xs font-semibold text-zinc-950 shadow-[0_6px_20px_-10px_rgba(255,255,255,0.8)] transition-transform hover:scale-[1.03] active:scale-95"
                            >
                                {installMethod === "android" ? "Install on Android" : "Install"}
                            </button>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

function IosInstallMicroGuide() {
    const { trigger } = useHaptic()
    const shouldReduceMotion = useReducedMotion()
    const [animationKey, setAnimationKey] = useState(0)

    function handleReplay() {
        trigger("light")
        setAnimationKey((currentKey) => currentKey + 1)
    }

    return (
        <button
            type="button"
            onClick={handleReplay}
            aria-label="Replay iOS install steps"
            className="relative mt-3 grid w-full grid-cols-[44px_1fr] items-center gap-3 rounded-xl border border-white/10 bg-zinc-900/75 p-2.5 text-left transition-transform active:scale-[0.98]"
        >
            <div className="flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-white/10 bg-zinc-950 text-sky-300 shadow-sm shadow-sky-950/30">
                <IosShareIcon className="h-6 w-6" />
            </div>

            <ShareSheetPreview key={animationKey} shouldReduceMotion={Boolean(shouldReduceMotion)} />
        </button>
    )
}

function ShareSheetPreview({ shouldReduceMotion }: { shouldReduceMotion: boolean }) {
    const sheetTransform = shouldReduceMotion ? "translate(0 -46)" : undefined
    const confirmationOpacity = shouldReduceMotion ? 1 : 0

    return (
        <svg
            viewBox="0 0 280 88"
            role="img"
            aria-label="Miniature iOS share sheet scrolling to Add to Home Screen"
            className="h-[74px] w-full overflow-hidden rounded-lg border border-white/10 bg-zinc-950/95"
        >
            <rect width="280" height="88" fill="rgb(9 9 11)" />
            <rect y="0" width="280" height="22" fill="rgb(24 24 27)" />
            <text x="12" y="15" fill="rgb(113 113 122)" fontSize="10" fontWeight="600">
                Share
            </text>
            <text x="250" y="15" fill="rgb(125 211 252)" fontSize="10" fontWeight="600" textAnchor="end">
                Done
            </text>
            <rect x="8" y="32" width="264" height="38" rx="9" fill="rgb(7 89 133)" fillOpacity="0.16" stroke="rgb(125 211 252)" strokeOpacity="0.65" />

            <g transform={sheetTransform}>
                {!shouldReduceMotion && (
                    <animateTransform
                        attributeName="transform"
                        type="translate"
                        values="0 0; 0 0; 0 -18; 0 -18; 0 -46; 0 -46"
                        keyTimes="0; 0.2; 0.38; 0.5; 0.72; 1"
                        dur="3.8s"
                        repeatCount="indefinite"
                        calcMode="spline"
                        keySplines="0.77 0 0.175 1; 0.77 0 0.175 1; 0.77 0 0.175 1; 0.77 0 0.175 1; 0.23 1 0.32 1"
                    />
                )}
                <ShareSheetSvgRow y={30} label="Copy" />
                <ShareSheetSvgRow y={66} label="Find on Page" />
                <ShareSheetSvgRow y={102} label="Add to Home Screen" emphasized />
                <ShareSheetSvgRow y={138} label="Add to Reading List" />
            </g>

            <g opacity={confirmationOpacity}>
                {!shouldReduceMotion && (
                    <animate
                        attributeName="opacity"
                        values="0; 0; 1; 1; 0"
                        keyTimes="0; 0.66; 0.74; 0.88; 1"
                        dur="3.8s"
                        repeatCount="indefinite"
                    />
                )}
                <circle cx="250" cy="51" r="12" fill="rgb(125 211 252)" />
                <path d="m244.5 51 3.4 3.4 7.6-8" fill="none" stroke="rgb(9 9 11)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </svg>
    )
}

function ShareSheetSvgRow({ emphasized = false, label, y }: { emphasized?: boolean; label: string; y: number }) {
    const fill = emphasized ? "rgb(12 74 110)" : "rgb(24 24 27)"
    const stroke = emphasized ? "rgb(125 211 252)" : "rgb(39 39 42)"
    const textFill = emphasized ? "rgb(244 244 245)" : "rgb(161 161 170)"

    return (
        <g>
            <rect x="12" y={y} width="256" height="28" rx="8" fill={fill} fillOpacity={emphasized ? 0.5 : 0.72} stroke={stroke} strokeOpacity={emphasized ? 0.7 : 1} />
            <rect x="20" y={y + 6} width="16" height="16" rx="4" fill="rgb(39 39 42)" />
            {emphasized ? <HomeGlyph x={23} y={y + 9} /> : <circle cx="28" cy={y + 14} r="2.5" fill="rgb(113 113 122)" />}
            <text x="44" y={y + 18} fill={textFill} fontSize="10" fontWeight="600">
                {label}
            </text>
            {emphasized && (
                <rect x="12" y={y} width="256" height="28" rx="8" fill="rgb(125 211 252)" opacity="0">
                    <animate attributeName="opacity" values="0; 0; 0.16; 0.16; 0" keyTimes="0; 0.56; 0.66; 0.78; 1" dur="3.8s" repeatCount="indefinite" />
                </rect>
            )}
        </g>
    )
}

function HomeGlyph({ x, y }: { x: number; y: number }) {
    return <path d={`M${x} ${y + 5}l5-4 5 4v6.5h-3.2v-4H${x + 3.2}v4H${x}z`} fill="rgb(212 212 216)" />
}

function IosShareIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
            <path d="M12 15V3" />
            <path d="M7.5 7.5 12 3l4.5 4.5" />
            <path d="M6 11v7.5A2.5 2.5 0 0 0 8.5 21h7A2.5 2.5 0 0 0 18 18.5V11" />
        </svg>
    )
}
